import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { toStructuredQuery, toMatchableData, matchItem } from 'teacher-ui-client/utils/search'
import { Product } from 'teacher-ui-client/utils/products'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { FastPhonicsNav } from 'teacher-ui-client/components/lessons/fast-phonics/nav'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { TilesPrecinctResourceEntry } from 'teacher-ui-client/components/tiles/precinct-resource-entry'
import { t } from 'ember-intl'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type FpLessonsVideosPage from 'teacher-ui-client/subscription-type/lessons/fast-phonics/videos/route'

interface Args {
  model: ModelFor<FpLessonsVideosPage>
}

interface PrecinctResourceViewModel {
  id: string
  title: string
  description?: string
  coverUrl?: string
  fileUrl?: string
  videoUrl?: string
}
export class FastPhonicsVideosPage extends Component<Args> {
  @service router!: RouterService

  product = Product.FP

  get precinctResourceViewModels(): PrecinctResourceViewModel[] {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    let entries = this.args.model.loaderInstance.value ?? []
    const query = this.router.currentRoute?.queryParams.searchQuery

    if (typeof query === 'string' && query.length > 0) {
      const structuredQuery = toStructuredQuery(query)

      entries = entries.filter((entry) => {
        const { title = '', description = '' } = entry.fields
        const matchableData = toMatchableData([title, description])

        return matchItem(matchableData, structuredQuery)
      })
    }

    return entries
      .map((entry) => {
        return {
          id: entry.sys.id,
          title: entry.fields.title,
          description: entry.fields.description,
          coverUrl: entry.fields.cover?.fields.file?.url,
          fileUrl: entry.fields.file?.fields.file?.url,
          videoUrl: entry.fields.videoUrl,
        }
      })
      .sort((a, b) => a.title.localeCompare(b.title))
  }

  <template>
    <LessonsPage
      @loaderInstance={{@model.loaderInstance}}
      @title={{t "pages.product.lessons.title" product=(t "products.fast-phonics")}}
    >
      <:header>
        <FastPhonicsNav />
        <LessonsSearch @themeKey={{ThemeKeys.blue}} />
      </:header>
      <:results>
        {{#each this.precinctResourceViewModels as |viewModel index|}}
          <TilesPrecinctResourceEntry
            data-test-precinct-resource-entry={{index}}
            class="w-80"
            @entryId={{viewModel.id}}
            @title={{viewModel.title}}
            @description={{viewModel.description}}
            @fileUrl={{viewModel.fileUrl}}
            @videoUrl={{viewModel.videoUrl}}
            @coverUrl={{viewModel.coverUrl}}
          />
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
      </:results>
    </LessonsPage>
  </template>
}

export default RouteTemplate(FastPhonicsVideosPage)
